import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BankStore, InsuranceStore } from '../../core/stores/base.store';
import { Bank, Insurance, InsuranceLoan } from '../../core/models';
import { formatDateAsYYYYMMDD, parseYYYYMMDDToDate } from 'app/core/utils/date.utils';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { NotificationUtils } from '../../core/utils';
import { LoanUtils } from '../../core/utils/loan.utils';

@Component({
  selector: 'app-insurance-loan-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    NumberFormatDirective
  ],
  templateUrl: './insurance-loan-payment-dialog.component.html',
  styleUrls: ['./insurance-loan-payment-dialog.component.css']
})
export class InsuranceLoanPaymentDialogComponent implements OnInit {
  paymentForm!: FormGroup;
  loading = false;
  
  private bankStore = inject(BankStore);
  private insuranceStore = inject(InsuranceStore);
  private fb = inject(FormBuilder);
  
  banks = this.bankStore.entities;
  
  constructor(
    public dialogRef: MatDialogRef<InsuranceLoanPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { insurance: Insurance }
  ) {}
  
  ngOnInit(): void {
    // Initialize the payment form
    this.paymentForm = this.fb.group({
      insurance_id: [this.data.insurance.id],
      loan_id: [null, Validators.required],
      date: [new Date(), Validators.required],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      bank_id: [null],
      payFromBank: [true] // Default to true for convenience
    });
    
    // Add conditional validation for bank_id based on payFromBank value
    this.paymentForm.get('payFromBank')?.valueChanges.subscribe(payFromBank => {
      const bankIdControl = this.paymentForm.get('bank_id');
      if (payFromBank) {
        bankIdControl?.setValidators([Validators.required]);
      } else {
        bankIdControl?.clearValidators();
      }
      bankIdControl?.updateValueAndValidity();
    });
    
    // Trigger initial validation based on default payFromBank value
    if (this.paymentForm.get('payFromBank')?.value) {
      this.paymentForm.get('bank_id')?.setValidators([Validators.required]);
    } else {
      this.paymentForm.get('bank_id')?.clearValidators();
    }
    this.paymentForm.get('bank_id')?.updateValueAndValidity();
  }
  
  /**
   * Handle form submission to record a loan payment
   */
  onSubmit(): void {
    if (this.paymentForm.invalid) {
      return;
    }
    
    this.loading = true;
    
    try {
      // Get the payment date and ensure it's a proper Date object
      const rawPaymentDate = this.paymentForm.get('date')?.value;
      const paymentDate = new Date(rawPaymentDate);
      paymentDate.setHours(12, 0, 0, 0);
      
      // Parse the amount using the utility function
      const amount = LoanUtils.parseFormattedNumber(this.paymentForm.get('amount')?.value);
      // Find the loan for the category
      const loanId = this.paymentForm.get('loan_id')?.value;
      const loan = this.data.insurance.insurance_loans_collection.find(l => l.id === loanId);
      loan.current_balance = LoanUtils.parseFormattedNumber(loan.current_balance) - amount;
      loan.interest_fee = LoanUtils.parseFormattedNumber(loan.interest_fee);
      loan.amount = LoanUtils.parseFormattedNumber(loan.amount);
      if (!loan) {
        throw new Error('Selected loan not found');
      }
      
      // Create the transaction object only if payFromBank is checked
      let transaction = null;
      if (this.paymentForm.get('payFromBank')?.value) {
        transaction = {
          insurance_id: this.paymentForm.get('insurance_id')?.value,
          insurance_loan_id: this.paymentForm.get('loan_id')?.value,
          transaction_date: formatDateAsYYYYMMDD(paymentDate),
          amount: amount,
          principal: 0,
          interest: 0,
          bank_id: this.paymentForm.get('bank_id')?.value,
          category: `Insurance Loan Payment`,
        };
      }
    
      
      // Close the dialog with the result
      this.dialogRef.close({
        transaction: transaction, // Will be null if payFromBank is false
        loan: loan
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      const notificationUtils = NotificationUtils.getInstance();
      notificationUtils.showError('Failed to process payment. Please try again.');
      this.loading = false;
    }
  }
  
  /**
   * Cancel the payment dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }
}
